import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  Sparkles, Sun, Moon, Star, Calendar, 
  ChevronRight, Loader2, Clock, MapPin, 
  Mic, MicOff, Send, Heart, BookOpen, Zap,
  ChevronDown, ChevronUp, Volume2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import AppLayout from '../components/AppLayout';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const AppDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictionLoading, setPredictionLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatResponse, setChatResponse] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [insightExpanded, setInsightExpanded] = useState(false);
  
  // Voice input states
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const recognitionRef = useRef(null);

  // Check voice support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setVoiceSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-IN';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setChatInput(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };
    }
  }, []);

  const toggleVoice = () => {
    if (!voiceSupported) return;
    
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (selectedProfile) {
      fetchPrediction(selectedProfile.profile_id);
    }
  }, [selectedProfile]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
      if (response.data.length > 0) {
        setSelectedProfile(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrediction = async (profileId) => {
    setPredictionLoading(true);
    try {
      const response = await axios.get(`${API}/predictions/${profileId}/daily`, { withCredentials: true });
      setPrediction(response.data);
    } catch (error) {
      console.error('Error fetching prediction:', error);
    } finally {
      setPredictionLoading(false);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim() || !selectedProfile) return;
    
    setChatLoading(true);
    try {
      const response = await axios.post(`${API}/chat/ask`, {
        message: chatInput,
        profile_id: selectedProfile.profile_id
      }, { withCredentials: true });
      setChatResponse(response.data.response);
      setChatInput('');
    } catch (error) {
      console.error('Chat error:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const quickActions = [
    { icon: BookOpen, label: 'Kundli', color: 'from-purple-500 to-pink-500', action: () => navigate('/janam-kundli') },
    { icon: Heart, label: 'Match', color: 'from-rose-500 to-red-500', action: () => navigate('/kundli-milan') },
  ];

  const quickQuestions = [
    "When will I get married?",
    "Career guidance",
    "Lucky days this week",
    "Love compatibility"
  ];

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#FFF9EF]' : 'bg-[#FFF9EF]'}`}>
        <Loader2 className="w-8 h-8 text-[#C84B32] animate-spin" />
      </div>
    );
  }

  return (
    <AppLayout title="Nakshatra">
      <div className="space-y-5">
        {/* Greeting Card with Expandable Insight */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-4 border ${
            isDark 
              ? 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-500/20' 
              : 'bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200'
          }`}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Welcome back,</p>
              <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{user?.name || 'Cosmic Soul'}</h2>
            </div>
          </div>
          
          {/* Expandable Insight Section */}
          {selectedProfile && prediction && (
            <div className={`rounded-xl p-3 mt-2 ${isDark ? 'bg-[#FCE9D9]/50' : 'bg-white/60'}`}>
              <button 
                onClick={() => setInsightExpanded(!insightExpanded)}
                className={`w-full flex items-center justify-between text-sm mb-2 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}
              >
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#C84B32]" />
                  <span>Today's Insight</span>
                </div>
                {insightExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  insightExpanded ? 'max-h-[500px]' : 'max-h-10'
                }`}
              >
                <p className={`text-sm leading-relaxed ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {prediction.prediction_summary}
                </p>
              </div>
              {!insightExpanded && prediction.prediction_summary?.length > 80 && (
                <button 
                  onClick={() => setInsightExpanded(true)}
                  className="text-[#C84B32] text-xs mt-1 font-medium"
                >
                  Tap to read more...
                </button>
              )}
            </div>
          )}
        </motion.div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action, index) => (
            <motion.button
              key={action.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              onClick={action.action}
              className={`flex items-center gap-3 rounded-2xl p-4 border ${
                isDark 
                  ? 'bg-white border-[#282622]/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}
              data-testid={`quick-action-${action.label.toLowerCase()}`}
            >
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center shadow-lg`}>
                <action.icon className="w-5 h-5 text-white" />
              </div>
              <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{action.label}</span>
            </motion.button>
          ))}
        </div>

        {/* AI Chat Section with Voice */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`rounded-2xl p-4 border ${
            isDark 
              ? 'bg-white border-[#282622]/10' 
              : 'bg-white border-gray-200 shadow-sm'
          }`}
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#C84B32]" />
            <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Ask Your Astrologer</h3>
            {voiceSupported && (
              <span className={`ml-auto flex items-center gap-1 text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                <Mic className="w-3 h-3" /> Voice enabled
              </span>
            )}
          </div>

          {/* Chat Response */}
          <AnimatePresence>
            {chatResponse && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className={`rounded-xl p-3 mb-3 border max-h-40 overflow-y-auto ${
                  isDark 
                    ? 'bg-purple-50 border-purple-500/20' 
                    : 'bg-purple-50 border-purple-200'
                }`}
              >
                <p className={`text-sm leading-relaxed ${isDark ? 'text-[#4A4640]' : 'text-gray-700'}`}>{chatResponse}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick Questions */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setChatInput(q)}
                className={`px-2.5 py-1 rounded-full text-[10px] transition-colors ${
                  isDark 
                    ? 'bg-[#FCE9D9]/40 text-[#6B6560] hover:bg-[#FCE9D9] hover:text-white' 
                    : 'bg-gray-100 text-[#6B6560] hover:bg-gray-200 hover:text-gray-700'
                }`}
              >
                {q}
              </button>
            ))}
          </div>

          {/* Chat Input with Voice */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleChat()}
                placeholder={isListening ? "Listening..." : "Ask about your future..."}
                className={`w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none ${
                  isListening 
                    ? 'border-red-500/50 bg-red-500/10' 
                    : isDark 
                      ? 'bg-black/50 border-[#282622]/10 text-white placeholder-gray-500 focus:border-purple-500/50' 
                      : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-purple-400'
                }`}
                data-testid="chat-input"
              />
            </div>
            
            {/* Voice Button */}
            {voiceSupported && (
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={toggleVoice}
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isListening 
                    ? 'bg-red-500 animate-pulse' 
                    : isDark 
                      ? 'bg-[#FCE9D9]/70 hover:bg-white/20' 
                      : 'bg-gray-100 hover:bg-gray-200'
                }`}
                data-testid="voice-btn"
              >
                {isListening ? (
                  <MicOff className="w-4 h-4 text-white" />
                ) : (
                  <Mic className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-600'}`} />
                )}
              </motion.button>
            )}
            
            {/* Send Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleChat}
              disabled={chatLoading || !chatInput.trim()}
              className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 flex items-center justify-center disabled:opacity-50"
              data-testid="send-btn"
            >
              {chatLoading ? (
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              ) : (
                <Send className="w-4 h-4 text-white" />
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Today's Prediction Card */}
        {prediction && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`rounded-2xl p-4 border ${
              isDark 
                ? 'bg-white border-[#282622]/10' 
                : 'bg-white border-gray-200 shadow-sm'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-500" />
                <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Today's Forecast</h3>
              </div>
              <span className={`text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>{new Date().toLocaleDateString()}</span>
            </div>

            <div className="space-y-3">
              {/* Lucky Elements */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-5 h-5 rounded-full border-2 border-[#282622]/15"
                    style={{ backgroundColor: prediction.lucky_color?.toLowerCase() || '#D4AF37' }}
                  />
                  <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>{prediction.lucky_color}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[#C84B32] font-bold text-sm">{prediction.lucky_number}</span>
                  <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Lucky #</span>
                </div>
              </div>

              {/* Advice */}
              {prediction.actionable_advice && (
                <div className={`rounded-xl p-2.5 border ${
                  isDark 
                    ? 'bg-amber-500/10 border-amber-500/20' 
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-amber-700' : 'text-amber-800'}`}>
                    <span className="font-semibold">Tip:</span> {prediction.actionable_advice}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Vedic Info Grid */}
        {prediction && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-2"
          >
            <div className={`rounded-xl p-3 border ${
              isDark ? 'bg-white border-[#282622]/10' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <Moon className="w-4 h-4 text-blue-600 mb-1" />
              <p className={`text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Moon Sign</p>
              <p className={`font-medium text-xs ${isDark ? 'text-white' : 'text-gray-800'}`}>{prediction.moon_sign}</p>
            </div>
            <div className={`rounded-xl p-3 border ${
              isDark ? 'bg-white border-[#282622]/10' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <Star className="w-4 h-4 text-purple-600 mb-1" />
              <p className={`text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Nakshatra</p>
              <p className={`font-medium text-xs ${isDark ? 'text-white' : 'text-gray-800'}`}>{prediction.nakshatra}</p>
            </div>
            <div className={`rounded-xl p-3 border ${
              isDark ? 'bg-white border-[#282622]/10' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <Sun className="w-4 h-4 text-amber-600 mb-1" />
              <p className={`text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Lagna</p>
              <p className={`font-medium text-xs ${isDark ? 'text-white' : 'text-gray-800'}`}>{prediction.lagna}</p>
            </div>
            <div className={`rounded-xl p-3 border ${
              isDark ? 'bg-white border-[#282622]/10' : 'bg-white border-gray-200 shadow-sm'
            }`}>
              <Calendar className="w-4 h-4 text-green-600 mb-1" />
              <p className={`text-[10px] ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>Dasha</p>
              <p className={`font-medium text-xs ${isDark ? 'text-white' : 'text-gray-800'}`}>{prediction.mahadasha}-{prediction.antardasha}</p>
            </div>
          </motion.div>
        )}
      </div>
    </AppLayout>
  );
};

export default AppDashboard;
